<template>
  <div>
    <ButtonTemplateMicInput text-button="마이크 설정" @click="toggleMicListModal">
      <template #icon-handler>
        <IconMic />
      </template>
    </ButtonTemplateMicInput>

    <!-- 마이크맵핑모달 -->
    <teleport to="body">
      <ModalSmall v-if="showModalMicMapping" title="마이크 설정" @close="toggleMicListModal">
        <div class="scroll-area">
          <div class="container-mapping-table">
            <h4 class="accent">마이크 고급설정</h4>
            <TableDynamic
              :columns="advancedOptionColumns"
              :rows="advancedOptionRows"
              :tag-classes="{}"
              :show-column-titles="true"
              :show-more-button="false"
            >
              <template v-slot:slot="{ row, rowIndex }">
                <div>
                  <input
                    class="input-small"
                    type="number"
                    :value="advancedOptionRows[rowIndex].value"
                    :min="row.min || 0"
                    :max="row.max || 10"
                    :id="row.id"
                    @input="onUpdateAdvancedOptionInput($event, row)"
                  />
                </div>
              </template>
            </TableDynamic>
          </div>
          <div class="container-mapping-table">
            <h4 class="accent">마이크 라벨</h4>
            <TableDynamic
              :columns="labelColumns"
              :rows="labelRows"
              :tag-classes="{}"
              :show-column-titles="true"
              :show-more-button="false"
              @updateInput="onUpdateLabelInput"
            >
              <template v-slot:slot="{ row }">
                <span :class="['status-dot', row.isConnected ? 'connected' : 'disconnected']" />
              </template>
            </TableDynamic>
          </div>
        </div>
        <div class="wrapper-buttons">
          <button class="gray-btn l-btn" @click="toggleMicListModal">
            {{ t('button_cancel') }}
          </button>
          <button class="primary-btn l-btn" @click="saveMicSettings">
            {{ t('button_save2') }}
          </button>
        </div>
      </ModalSmall>
    </teleport>
  </div>
</template>

<script setup lang="ts">
import { ref, watch, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';

import { cloneDeep } from 'lodash';
import { storeToRefs } from 'pinia';

import { fetchMicLabelList, updateMicLabelList } from '@/api/apiService';
import ButtonTemplateMicInput from '@/components/domains/chat/common/ButtonTemplateMicInput.vue';
import IconMic from '@/components/icons/IconMic.vue';
import TableDynamic from '@/components/shared/base/TableDynamic.vue';
import ModalSmall from '@/components/shared/composite/ModalSmall.vue';
import { useMicrophonesStore } from '@/stores/microphones';
import { useSocketStore } from '@/stores/socket';
import { IMicrophone } from '@/types/components';
import { IMicLabelResponse } from '@/types/response';

const socketStore = useSocketStore();
const microphonesStore = useMicrophonesStore();
const { microphones } = storeToRefs(microphonesStore);

const { t } = useI18n();

// 모달 열림 상태
const showModalMicMapping = ref<boolean>(false);
const originMicrophones = ref<IMicrophone[]>([]);

// 마이크 고급설정 (EVAD)
const advancedOptionRows = ref([
  {
    key: 'upperThreshold',
    value: -25,
    label: '상향값',
    min: -60,
    max: -5,
  },
  {
    key: 'lowerThresholdLead',
    value: -45.0,
    label: '하향리드값',
    min: -60,
    max: -5,
  },
  {
    key: 'lowerThresholdTail',
    value: -45.0,
    label: '하향테일값',
    min: -5,
    max: -60,
  },
  {
    key: 'vadThreshold',
    value: -45.0,
    label: 'VAD',
    min: -5,
    max: -60,
  },
]);
// 마이크 라벨
const labelRows = ref<Pick<IMicrophone, 'id' | 'label' | 'customLabel'>[]>([]);

// 마이크 고급설정 (EVAD)
const advancedOptionColumns = [
  { key: 'label', label: 'EVAD', class: 'width70' },
  { key: 'value', label: '현재 수치', type: 'slot', class: 'width30' },
];
// 마이크 라벨
const labelColumns = [
  { key: 'isConnected', label: '', class: 'width10 paddingleft', type: 'slot' },
  { key: 'label', label: '마이크', class: 'width50' },
  {
    key: 'customLabel',
    label: '라벨',
    type: 'input-text',
    class: 'width30',
  },
];

const initRows = () => {
  labelRows.value = cloneDeep(microphones.value);
};

const toggleMicListModal = () => {
  showModalMicMapping.value = !showModalMicMapping.value;

  if (showModalMicMapping.value) {
    initRows();
  }
};

// 마이크 고급설정 (EVAD) Input event
const onUpdateAdvancedOptionInput = (e, row) => {
  const inx = advancedOptionRows.value.findIndex((option) => row.key === option.key);
  if (inx !== -1) {
    advancedOptionRows.value[inx].value = Number(e.target?.value);
  }
};

// 마이크 라벨 Input event
const onUpdateLabelInput = ({ row, key, value }) => {
  const inx = labelRows.value.findIndex(({ id }) => row.id === id);
  if (inx !== -1) {
    labelRows.value[inx][key] = value;
  }
};

// 마이크 고급설정 초기화
const initAdvancedOptionRows = () => {
  advancedOptionRows.value.forEach((opt) => {
    const is = socketStore.micAdvancedOptions[opt.key];
    if (is) {
      opt.value = is;
    }
  });
};

// 마이크 라벨 초기화
const initCustomMicLabels = async (list: IMicLabelResponse[]) => {
  const micList = cloneDeep(list);
  const micListWithCustomLabel = [...microphones.value].map((mic) => {
    const { customLabel = '' } = micList.find(({ label }, micIndex) => {
      if (mic.label === label) {
        micList.splice(micIndex, 1);
      }
      return mic.label === label;
    }) || { customLabel: '' };
    return {
      ...mic,
      customLabel: customLabel.trim(),
    };
  });
  microphones.value = cloneDeep(micListWithCustomLabel);
};

// 마이크 고급설정 저장
const saveAdvancedOptions = () => {
  const updateOptions = advancedOptionRows.value.reduce(
    (acc, option) => {
      if (option) {
        acc[option.key] = option.value;
      }
      return acc;
    },
    {
      upperThreshold: 0,
      lowerThresholdLead: 0,
      lowerThresholdTail: 0,
      vadThreshold: 0,
    }
  );
  socketStore.sendMessageEp(updateOptions);
};

// 마이크 라벨 저장
const saveCustomMicLabels = async (list: Pick<IMicrophone, 'id' | 'label' | 'customLabel'>[]) => {
  await initCustomMicLabels(list);

  const cloneOriginMicrophones = cloneDeep([...originMicrophones.value]);
  const updateMicList = list.reduce((acc: IMicLabelResponse[], { label, customLabel }: IMicLabelResponse) => {
    const inx = cloneOriginMicrophones.findIndex((origin) => origin.label === label);
    let originCustomLabel = '';

    if (inx !== -1) {
      const ddd = cloneOriginMicrophones.splice(inx, 1)[0];
      originCustomLabel = ddd.customLabel;
    }

    if ((inx === -1 && label && customLabel) || (inx !== -1 && originCustomLabel !== customLabel)) {
      acc.push({ label, customLabel });
    }

    return acc;
  }, []);

  if (updateMicList.length > 0) {
    await updateMicLabelList(updateMicList);
  }

  originMicrophones.value = cloneDeep(microphones.value);
  toggleMicListModal();
};

// 마이크 설정 저장
const saveMicSettings = async () => {
  saveAdvancedOptions();
  await saveCustomMicLabels(labelRows.value);
};

// 기존 마이크 라벨 요청
const fetchPreviousMicLabel = async () => {
  const { data = [], success } = await fetchMicLabelList();
  if (success) {
    await initCustomMicLabels(data);
    originMicrophones.value = cloneDeep(microphones.value);
  }
};

onMounted(async () => {
  await fetchPreviousMicLabel();
  initAdvancedOptionRows();
});

watch(
  () => microphones.value,
  async (val, oldVal) => {
    if (val.length !== oldVal.length) {
      await fetchPreviousMicLabel();
    }
    initRows();
  }
);
</script>

<style scoped lang="scss">
::v-deep(.dynamic-table) {
  min-width: unset;
}

.container-mapping-table {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-bottom: 1rem;
}

.wrapper-buttons {
  display: flex;
  gap: 0.5rem;
  margin-top: 1rem;
  align-self: flex-end;
}

.status-dot {
  width: 0.75rem;
  height: 0.75rem;
  border-radius: 50%;
  flex-shrink: 0;
  display: inline-block;

  &.connected {
    background-color: var(--success);
  }
  &.disconnected {
    background-color: var(--surface-gray);
  }
}

.scroll-area {
  max-height: 440px;
  overflow-y: auto;
}
</style>
